import { useState, useRef } from "react";
import {
  Plus, Trash2, ChevronUp, ChevronDown, Save, Loader2, GripVertical, FileEdit,
  Power, PowerOff, Archive, Copy, Settings2, Eye, EyeOff, Search, X, Pencil,
  Check, ListChecks, SlidersHorizontal, ArrowLeft, Layers, CornerDownRight,
} from "lucide-react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import ConfirmDialog from "@/components/ConfirmDialog";
import SalesPreview from "@/components/automation/SalesPreview";
import RulesDialog from "@/components/automation/RulesDialog";

const FIELD_MAPPINGS = ["", "service_interested", "budget_range", "business_segment", "business_category"];
const inp = "h-9 w-full rounded-lg border border-sky bg-white px-2.5 text-sm outline-none focus:border-loop focus:ring-2 focus:ring-loop/20";
const lbl = "mb-1 block text-[11px] font-semibold uppercase tracking-wide text-ink/50";

const STATUS_BADGE = {
  Active: "bg-emerald-50 text-emerald-700",
  Inactive: "bg-slate-100 text-slate-500",
  Archived: "bg-amber-50 text-amber-700",
};
const FILTER_TABS = ["Active", "Inactive", "Archived", "All"];
const STEP_GENERAL = "General";
const OPTION_TYPES = ["Single Select", "Multi Select"];

const newId = () => `q${Date.now().toString(36)}${Math.floor(Math.random() * 1000)}`;
const blankFlow = () => ({ name: "", description: "", status: "Active", trigger_stages: [], questions: [], hard_rules: [] });
const blankQuestion = (step) => ({ question_id: newId(), question_label: "", question_type: "Single Select", options: [], required: true, scoring_enabled: false, field_mapping: null, category_dependent: false, step: step && step !== STEP_GENERAL ? step : null, conditional: null, visibility_conditions: [] });
const blankRule = () => ({ name: "", conditions: [["", "equals", ""]], action: "cap_quality", target: "Queen" });

const stepOf = (q) => q.step || STEP_GENERAL;
const computeSteps = (questions) => {
  const seen = [];
  (questions || []).forEach((q) => { const s = stepOf(q); if (!seen.includes(s)) seen.push(s); });
  if (!seen.length) seen.push(STEP_GENERAL);
  return seen;
};

export default function FlowBuilder({ flows, meta, options, onChanged }) {
  const [draft, setDraft] = useState(null);
  const [stepOrder, setStepOrder] = useState([STEP_GENERAL]);
  const [toDelete, setToDelete] = useState(null);
  const [toToggle, setToToggle] = useState(null);
  const [statusFilter, setStatusFilter] = useState("Active");
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  // editor UI state
  const [selectedQid, setSelectedQid] = useState(null);
  const [activeStep, setActiveStep] = useState(STEP_GENERAL);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [flagFilter, setFlagFilter] = useState("all"); // all | required | scored | conditional
  const [previewMode, setPreviewMode] = useState(false);
  const [rulesOpen, setRulesOpen] = useState(false);
  const [renamingStep, setRenamingStep] = useState(null);
  const [renameVal, setRenameVal] = useState("");
  const [dragId, setDragId] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);
  const canvasRef = useRef(null);

  const startEdit = (flow) => {
    const copy = JSON.parse(JSON.stringify(flow));
    setEditingId(flow.id); setDraft(copy);
    const steps = computeSteps(copy.questions);
    setStepOrder(steps); setActiveStep(steps[0]); setSelectedQid(null);
    setSearch(""); setTypeFilter("all"); setFlagFilter("all"); setPreviewMode(false);
  };
  const startNew = () => {
    setEditingId("new"); setDraft(blankFlow());
    setStepOrder([STEP_GENERAL]); setActiveStep(STEP_GENERAL); setSelectedQid(null);
    setSearch(""); setTypeFilter("all"); setFlagFilter("all"); setPreviewMode(false);
  };
  const cancel = () => { setEditingId(null); setDraft(null); setSelectedQid(null); };

  const upd = (patch) => setDraft((d) => ({ ...d, ...patch }));
  const updQById = (qid, patch) => setDraft((d) => ({ ...d, questions: d.questions.map((q) => q.question_id === qid ? { ...q, ...patch } : q) }));
  const updR = (i, patch) => setDraft((d) => ({ ...d, hard_rules: d.hard_rules.map((r, idx) => idx === i ? { ...r, ...patch } : r) }));

  /* ---- step ops ---- */
  const addStep = () => {
    let n = 1, name;
    do { name = `Step ${n++}`; } while (stepOrder.includes(name));
    setStepOrder((s) => [...s, name]); setActiveStep(name);
    setTimeout(() => document.getElementById(`step-sec-${name}`)?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
  };
  const moveStep = (idx, dir) => setStepOrder((s) => { const a = [...s]; const j = idx + dir; if (j < 0 || j >= a.length) return s; [a[idx], a[j]] = [a[j], a[idx]]; return a; });
  const commitRename = (oldName) => {
    const nn = renameVal.trim();
    setRenamingStep(null);
    if (!nn || nn === oldName) return;
    if (stepOrder.includes(nn)) { toast.error("A step with that name already exists"); return; }
    setStepOrder((s) => s.map((x) => x === oldName ? nn : x));
    setDraft((d) => ({ ...d, questions: d.questions.map((q) => stepOf(q) === oldName ? { ...q, step: nn } : q) }));
    setActiveStep((a) => a === oldName ? nn : a);
  };
  const deleteStep = (name) => {
    if (stepOrder.length <= 1) { toast.error("At least one step is required"); return; }
    setDraft((d) => ({ ...d, questions: d.questions.map((q) => stepOf(q) === name ? { ...q, step: null } : q) }));
    setStepOrder((s) => { let ns = s.filter((x) => x !== name); if (!ns.includes(STEP_GENERAL)) ns = [STEP_GENERAL, ...ns]; return ns; });
    setActiveStep((a) => a === name ? STEP_GENERAL : a);
  };

  /* ---- question ops ---- */
  const addQuestion = (step) => {
    const q = blankQuestion(step);
    setDraft((d) => ({ ...d, questions: [...d.questions, q] }));
    setSelectedQid(q.question_id);
  };
  const duplicateQuestion = (qid) => setDraft((d) => {
    const i = d.questions.findIndex((q) => q.question_id === qid); if (i < 0) return d;
    const copy = JSON.parse(JSON.stringify(d.questions[i]));
    copy.question_id = newId();
    copy.question_label = copy.question_label ? `${copy.question_label} (copy)` : "";
    const arr = [...d.questions]; arr.splice(i + 1, 0, copy); return { ...d, questions: arr };
  });
  const deleteQuestion = (qid) => { setDraft((d) => ({ ...d, questions: d.questions.filter((q) => q.question_id !== qid) })); if (selectedQid === qid) setSelectedQid(null); };
  const moveQuestion = (qid, dir) => setDraft((d) => {
    const q = d.questions.find((x) => x.question_id === qid); if (!q) return d;
    const step = stepOf(q);
    const ids = d.questions.filter((x) => stepOf(x) === step).map((x) => x.question_id);
    const np = ids.indexOf(qid) + dir; if (np < 0 || np >= ids.length) return d;
    const arr = [...d.questions];
    const ia = arr.findIndex((x) => x.question_id === qid);
    const ib = arr.findIndex((x) => x.question_id === ids[np]);
    [arr[ia], arr[ib]] = [arr[ib], arr[ia]]; return { ...d, questions: arr };
  });
  const reorderQuestion = (drag, targetId, targetStep) => setDraft((d) => {
    if (drag === targetId) return d;
    const arr = [...d.questions];
    const from = arr.findIndex((q) => q.question_id === drag); if (from < 0) return d;
    const [moved] = arr.splice(from, 1);
    moved.step = targetStep && targetStep !== STEP_GENERAL ? targetStep : null;
    let to = targetId ? arr.findIndex((q) => q.question_id === targetId) : arr.length;
    if (to < 0) to = arr.length;
    arr.splice(to, 0, moved); return { ...d, questions: arr };
  });

  const toggleStage = (s) => setDraft((d) => ({ ...d, trigger_stages: d.trigger_stages.includes(s) ? d.trigger_stages.filter((x) => x !== s) : [...d.trigger_stages, s] }));

  const orderedQuestions = () => {
    const out = [];
    stepOrder.forEach((s) => draft.questions.filter((q) => stepOf(q) === s).forEach((q) => out.push(q)));
    draft.questions.forEach((q) => { if (!out.includes(q)) out.push(q); });
    return out;
  };

  const save = async () => {
    if (!draft.name.trim()) return toast.error("Flow name is required");
    const questions = orderedQuestions().map((q, i) => ({
      ...q, question_id: q.question_id || newId(), display_order: i + 1,
      options: q.options || [], visibility_conditions: q.visibility_conditions || [],
    }));
    const payload = { ...draft, questions };
    setSaving(true);
    try {
      if (editingId === "new") await api.post("/automation/flows", payload);
      else await api.put(`/automation/flows/${editingId}`, payload);
      toast.success("Flow saved"); cancel(); onChanged();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const del = async () => {
    try { await api.delete(`/automation/flows/${toDelete.id}`); toast.success("Flow deleted"); onChanged(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const applyStatus = async () => {
    if (!toToggle) return;
    try {
      await api.post(`/automation/flows/${toToggle.flow.id}/status`, { status: toToggle.status });
      toast.success(toToggle.status === "Active" ? "Flow activated" : toToggle.status === "Archived" ? "Flow archived" : "Flow deactivated");
      setToToggle(null); onChanged();
    } catch (e) { toast.error(apiError(e)); setToToggle(null); }
  };

  // ---------- LIST VIEW ----------
  if (!draft) {
    const filtered = statusFilter === "All" ? flows : flows.filter((f) => (f.status || "Active") === statusFilter);
    const toggleCopy = toToggle?.status === "Active"
      ? "This flow will become available again for new lead qualifications. Existing lead answers are unaffected."
      : toToggle?.status === "Archived"
        ? "This flow will be archived and hidden from new lead qualifications. Existing lead answers remain safe and visible."
        : "This flow will no longer be available for new qualifications. Existing lead answers will remain safe and visible.";
    return (
      <div data-testid="flow-builder">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-ink/60">Define the guided questionnaires used to qualify leads & auto-score quality.</p>
          <button onClick={startNew} data-testid="flow-new-button" className="flex items-center gap-1.5 rounded-lg bg-loop px-3 py-2 text-sm font-semibold text-white hover:bg-loophover"><Plus className="h-4 w-4" />New Flow</button>
        </div>
        <div className="mb-4 flex flex-wrap gap-1.5" data-testid="flow-status-filter">
          {FILTER_TABS.map((t) => {
            const count = t === "All" ? flows.length : flows.filter((f) => (f.status || "Active") === t).length;
            return (
              <button key={t} data-testid={`flow-filter-${t.toLowerCase()}`} onClick={() => setStatusFilter(t)}
                className={`rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${statusFilter === t ? "bg-navy text-white" : "border border-sky bg-white text-ink/60 hover:bg-mist"}`}>
                {t} <span className="opacity-60">{count}</span>
              </button>
            );
          })}
        </div>
        {filtered.length === 0 && <p className="rounded-2xl border border-dashed border-sky py-10 text-center text-sm text-ink/40" data-testid="flow-empty">No {statusFilter !== "All" ? statusFilter.toLowerCase() : ""} flows.</p>}
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
          {filtered.map((f) => {
            const status = f.status || "Active";
            const locked = f.has_submissions;
            return (
            <div key={f.id} data-testid={`flow-card-${f.key}`} className="flex flex-col rounded-2xl border border-sky/70 bg-white p-4">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <h3 className="font-sora text-sm font-bold text-navy">{f.name}</h3>
                  <p className="mt-0.5 text-xs text-ink/50 line-clamp-2">{f.description || "—"}</p>
                </div>
                <span data-testid={`flow-status-${f.key}`} className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold ${STATUS_BADGE[status] || STATUS_BADGE.Inactive}`}>{status}</span>
              </div>
              <div className="mt-3 flex items-center gap-3 text-[11px] text-ink/50">
                <span>{f.questions?.length || 0} questions</span><span>·</span>
                <span>{[...new Set((f.questions || []).map(stepOf))].length} steps</span><span>·</span>
                <span>{f.hard_rules?.length || 0} rules</span>
                {locked && (<><span>·</span><span data-testid={`flow-usage-${f.key}`} className="text-ink/45">{f.usage_count || 0} qualified</span></>)}
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                <button onClick={() => startEdit(f)} data-testid={`flow-edit-${f.key}`} className="flex flex-1 items-center justify-center gap-1 rounded-lg border border-sky px-2 py-1.5 text-xs font-semibold text-loop hover:bg-sky/30"><FileEdit className="h-3.5 w-3.5" />Edit</button>
                {status === "Active" ? (
                  <button onClick={() => setToToggle({ flow: f, status: "Inactive" })} data-testid={`flow-deactivate-${f.key}`} title="Deactivate" className="flex items-center justify-center gap-1 rounded-lg border border-sky px-2.5 py-1.5 text-xs font-semibold text-slate-500 hover:bg-mist"><PowerOff className="h-3.5 w-3.5" />Deactivate</button>
                ) : (
                  <button onClick={() => setToToggle({ flow: f, status: "Active" })} data-testid={`flow-activate-${f.key}`} title="Activate" className="flex items-center justify-center gap-1 rounded-lg border border-emerald-200 px-2.5 py-1.5 text-xs font-semibold text-emerald-700 hover:bg-emerald-50"><Power className="h-3.5 w-3.5" />Activate</button>
                )}
                {status !== "Archived" && (
                  <button onClick={() => setToToggle({ flow: f, status: "Archived" })} data-testid={`flow-archive-${f.key}`} title="Archive" className="rounded-lg border border-sky p-1.5 text-amber-600 hover:bg-amber-50"><Archive className="h-3.5 w-3.5" /></button>
                )}
                <button onClick={() => !locked && setToDelete(f)} disabled={locked} data-testid={`flow-delete-${f.key}`} title={locked ? "Used by leads — deactivate/archive instead" : "Delete"} className={`rounded-lg border border-sky p-1.5 ${locked ? "cursor-not-allowed text-slate-300" : "text-red-500 hover:bg-red-50"}`}><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            </div>
          );})}
        </div>
        <ConfirmDialog open={!!toDelete} onOpenChange={(v) => !v && setToDelete(null)} onConfirm={del} title="Delete this flow?" description={toDelete ? `"${toDelete.name}" and its questions will be permanently deleted. This cannot be undone.` : ""} confirmLabel="Delete flow" testid="delete-flow-dialog" />
        <ConfirmDialog open={!!toToggle} onOpenChange={(v) => !v && setToToggle(null)} onConfirm={applyStatus} title={toToggle?.status === "Active" ? "Activate this flow?" : toToggle?.status === "Archived" ? "Archive this flow?" : "Deactivate this flow?"} description={toToggle ? `"${toToggle.flow.name}" — ${toggleCopy}` : ""} confirmLabel={toToggle?.status === "Active" ? "Activate flow" : toToggle?.status === "Archived" ? "Archive flow" : "Deactivate flow"} destructive={toToggle?.status !== "Active"} testid="toggle-flow-dialog" />
      </div>
    );
  }

  // ---------- EDITOR VIEW (Visual Step Builder) ----------
  const qTypes = meta?.question_types || ["Single Select", "Multi Select", "Yes/No", "Text", "Long Text", "Number", "Currency", "Date"];
  const qIdOptions = draft.questions.map((q) => ({ id: q.question_id, label: q.question_label || q.question_id }));
  const selectedQ = draft.questions.find((q) => q.question_id === selectedQid) || null;

  const matchQ = (q) => {
    if (search && !(q.question_label || "").toLowerCase().includes(search.toLowerCase())) return false;
    if (typeFilter !== "all" && q.question_type !== typeFilter) return false;
    if (flagFilter === "required" && !q.required) return false;
    if (flagFilter === "scored" && !q.scoring_enabled) return false;
    if (flagFilter === "conditional" && !q.conditional?.question_id) return false;
    return true;
  };
  const filtering = !!search || typeFilter !== "all" || flagFilter !== "all";
  const totalMatches = draft.questions.filter(matchQ).length;

  return (
    <div data-testid="flow-editor">
      {/* Top bar */}
      <div className="mb-4 flex flex-wrap items-center gap-2 rounded-2xl border border-sky/70 bg-white p-3">
        <button onClick={cancel} className="flex items-center gap-1 rounded-lg border border-sky px-2.5 py-2 text-sm font-semibold text-ink/70 hover:bg-mist"><ArrowLeft className="h-4 w-4" />Back</button>
        <input data-testid="flow-name-input" className="h-10 min-w-[160px] flex-1 rounded-lg border border-sky bg-white px-3 font-sora text-sm font-bold text-navy outline-none focus:border-loop focus:ring-2 focus:ring-loop/20" placeholder="Flow name" value={draft.name} onChange={(e) => upd({ name: e.target.value })} />
        <select className="h-10 rounded-lg border border-sky bg-white px-2 text-sm font-semibold text-ink/70 outline-none focus:border-loop" value={draft.status} onChange={(e) => upd({ status: e.target.value })} data-testid="flow-status-select">
          <option value="Active">Active</option><option value="Inactive">Inactive</option><option value="Archived">Archived</option>
        </select>
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-ink/40" />
          <input data-testid="flow-search" className="h-10 w-44 rounded-lg border border-sky bg-white pl-8 pr-7 text-sm outline-none focus:border-loop focus:ring-2 focus:ring-loop/20" placeholder="Search questions" value={search} onChange={(e) => setSearch(e.target.value)} />
          {search && <button onClick={() => setSearch("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-ink/40 hover:text-ink"><X className="h-3.5 w-3.5" /></button>}
        </div>
        <button onClick={() => setRulesOpen(true)} data-testid="flow-rules-button" className="flex items-center gap-1.5 rounded-lg border border-sky px-2.5 py-2 text-sm font-semibold text-ink/70 hover:bg-mist"><ListChecks className="h-4 w-4" />Rules<span className="rounded-full bg-mist px-1.5 text-[11px]">{draft.hard_rules.length}</span></button>
        <button onClick={() => setPreviewMode((p) => !p)} data-testid="flow-preview-toggle" className={`flex items-center gap-1.5 rounded-lg border px-2.5 py-2 text-sm font-semibold ${previewMode ? "border-loop bg-loop/10 text-loop" : "border-sky text-ink/70 hover:bg-mist"}`}>{previewMode ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}Preview</button>
        <button onClick={save} disabled={saving} data-testid="flow-save-button" className="flex items-center gap-1.5 rounded-lg bg-loop px-4 py-2 text-sm font-semibold text-white hover:bg-loophover disabled:opacity-60">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}Save</button>
      </div>

      {previewMode ? (
        <div className="rounded-2xl border border-sky/70 bg-mist/30 p-6" data-testid="flow-preview-container">
          <SalesPreview draft={{ ...draft, questions: orderedQuestions() }} stepOrder={stepOrder} options={options} />
        </div>
      ) : (
      <div className="flex gap-4">
        {/* LEFT PANEL — Steps & nav */}
        <aside className="w-60 shrink-0" data-testid="flow-left-panel">
          <div className="mb-3 rounded-xl border border-sky/70 bg-white p-2.5">
            <label className={lbl}>Description</label>
            <input className={inp} value={draft.description} onChange={(e) => upd({ description: e.target.value })} placeholder="Short description" data-testid="flow-description-input" />
            <label className={`${lbl} mt-2`}>Trigger stages</label>
            <div className="flex flex-wrap gap-1">
              {(options?.stages || []).map((s) => {
                const on = draft.trigger_stages.includes(s);
                return <button key={s} type="button" onClick={() => toggleStage(s)} className={`rounded-full border px-2 py-0.5 text-[10px] font-medium ${on ? "border-loop bg-loop text-white" : "border-sky bg-white text-ink/60 hover:bg-mist"}`}>{options?.stage_meta?.[s]?.name || s}</button>;
              })}
            </div>
          </div>

          <div className="mb-2 grid grid-cols-2 gap-1.5">
            <select className="h-8 rounded-lg border border-sky bg-white px-1.5 text-xs outline-none focus:border-loop" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} data-testid="flow-filter-type">
              <option value="all">All types</option>
              {qTypes.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
            <select className="h-8 rounded-lg border border-sky bg-white px-1.5 text-xs outline-none focus:border-loop" value={flagFilter} onChange={(e) => setFlagFilter(e.target.value)} data-testid="flow-filter-flag">
              <option value="all">Any</option><option value="required">Required</option><option value="scored">Scored</option><option value="conditional">Conditional</option>
            </select>
          </div>
          {filtering && <p className="mb-2 text-[11px] font-semibold text-loop" data-testid="flow-match-count">{totalMatches} match{totalMatches === 1 ? "" : "es"}</p>}

          <div className="mb-2 flex items-center justify-between">
            <span className="flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide text-ink/50"><Layers className="h-3.5 w-3.5" />Steps</span>
            <button onClick={addStep} data-testid="add-step-button" className="flex items-center gap-1 rounded-lg border border-sky px-1.5 py-1 text-[11px] font-semibold text-loop hover:bg-sky/30"><Plus className="h-3 w-3" />Step</button>
          </div>
          <div className="space-y-1">
            {stepOrder.map((s, si) => {
              const count = draft.questions.filter((q) => stepOf(q) === s).length;
              const isActive = activeStep === s;
              return (
                <div key={s} data-testid={`step-nav-${si}`}
                  onDragOver={(e) => { if (dragId) e.preventDefault(); }}
                  onDrop={(e) => { e.preventDefault(); if (dragId) { reorderQuestion(dragId, null, s); setDragId(null); setDragOverId(null); } }}
                  className={`rounded-lg border px-2 py-1.5 transition-colors ${isActive ? "border-loop bg-loop/5" : "border-sky/60 bg-white hover:bg-mist"}`}>
                  {renamingStep === s ? (
                    <div className="flex items-center gap-1">
                      <input autoFocus className="h-7 w-full rounded border border-loop px-1.5 text-xs outline-none" value={renameVal} onChange={(e) => setRenameVal(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") commitRename(s); if (e.key === "Escape") setRenamingStep(null); }} data-testid={`step-rename-input-${si}`} />
                      <button onClick={() => commitRename(s)} className="text-emerald-600"><Check className="h-4 w-4" /></button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1">
                      <button onClick={() => { setActiveStep(s); document.getElementById(`step-sec-${s}`)?.scrollIntoView({ behavior: "smooth", block: "start" }); }} className="flex min-w-0 flex-1 items-center gap-1 text-left" data-testid={`step-nav-btn-${si}`}>
                        <span className={`truncate text-xs font-semibold ${isActive ? "text-loop" : "text-navy"}`}>{s}</span>
                        <span className="shrink-0 rounded-full bg-mist px-1.5 text-[10px] text-ink/50">{count}</span>
                      </button>
                      <button onClick={() => moveStep(si, -1)} disabled={si === 0} className="text-ink/30 hover:text-ink disabled:opacity-30"><ChevronUp className="h-3.5 w-3.5" /></button>
                      <button onClick={() => moveStep(si, 1)} disabled={si === stepOrder.length - 1} className="text-ink/30 hover:text-ink disabled:opacity-30"><ChevronDown className="h-3.5 w-3.5" /></button>
                      <button onClick={() => { setRenamingStep(s); setRenameVal(s); }} data-testid={`step-rename-${si}`} className="text-ink/30 hover:text-loop"><Pencil className="h-3 w-3" /></button>
                      <button onClick={() => deleteStep(s)} data-testid={`step-delete-${si}`} className="text-ink/30 hover:text-red-500"><Trash2 className="h-3 w-3" /></button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </aside>

        {/* CENTER — visual canvas */}
        <main ref={canvasRef} className="min-w-0 flex-1 space-y-5 overflow-y-auto pr-1" style={{ maxHeight: "calc(100vh - 240px)" }} data-testid="flow-canvas">
          {stepOrder.map((s) => {
            const stepQs = draft.questions.filter((q) => stepOf(q) === s);
            const shown = stepQs.filter(matchQ);
            if (filtering && shown.length === 0) return null;
            return (
              <section key={s} id={`step-sec-${s}`} data-testid={`step-section-${s}`}
                onDragOver={(e) => { if (dragId) e.preventDefault(); }}
                onDrop={(e) => { e.preventDefault(); if (dragId) { reorderQuestion(dragId, null, s); setDragId(null); setDragOverId(null); } }}>
                <div className="mb-2 flex items-center justify-between">
                  <h3 className="flex items-center gap-1.5 font-sora text-sm font-bold text-navy"><CornerDownRight className="h-4 w-4 text-loop" />{s}<span className="rounded-full bg-mist px-2 text-[11px] font-medium text-ink/50">{stepQs.length}</span></h3>
                  <button onClick={() => addQuestion(s)} data-testid={`add-question-${s}`} className="flex items-center gap-1 rounded-lg border border-sky px-2 py-1 text-[11px] font-semibold text-loop hover:bg-sky/30"><Plus className="h-3 w-3" />Add question</button>
                </div>
                {stepQs.length === 0 && <p className="rounded-xl border border-dashed border-sky py-6 text-center text-xs text-ink/40">Drop a question here or click "Add question".</p>}
                <div className="space-y-2">
                  {shown.map((q) => {
                    const gi = draft.questions.findIndex((x) => x.question_id === q.question_id);
                    const isSel = selectedQid === q.question_id;
                    return (
                      <div key={q.question_id} data-testid={`question-card-${gi}`}
                        draggable
                        onDragStart={() => setDragId(q.question_id)}
                        onDragEnd={() => { setDragId(null); setDragOverId(null); }}
                        onDragOver={(e) => { if (dragId && dragId !== q.question_id) { e.preventDefault(); setDragOverId(q.question_id); } }}
                        onDrop={(e) => { e.preventDefault(); e.stopPropagation(); if (dragId) { reorderQuestion(dragId, q.question_id, s); setDragId(null); setDragOverId(null); } }}
                        onClick={() => setSelectedQid(q.question_id)}
                        className={`cursor-pointer rounded-xl border bg-white p-3 transition-all ${isSel ? "border-loop ring-2 ring-loop/20" : "border-sky/60 hover:border-loop/50"} ${dragOverId === q.question_id ? "border-t-2 border-t-loop" : ""}`}>
                        <div className="flex items-center gap-2">
                          <GripVertical className="h-4 w-4 shrink-0 cursor-grab text-ink/30" />
                          <input className={`${inp} flex-1`} placeholder="Question label" value={q.question_label} onChange={(e) => updQById(q.question_id, { question_label: e.target.value })} onClick={(e) => e.stopPropagation()} data-testid={`question-label-${gi}`} />
                          <select className="h-9 w-32 rounded-lg border border-sky bg-white px-1.5 text-xs outline-none focus:border-loop" value={q.question_type} onChange={(e) => updQById(q.question_id, { question_type: e.target.value })} onClick={(e) => e.stopPropagation()} data-testid={`question-type-${gi}`}>
                            {qTypes.map((t) => <option key={t} value={t}>{t}</option>)}
                          </select>
                          <button onClick={(e) => { e.stopPropagation(); moveQuestion(q.question_id, -1); }} className="rounded p-1 text-ink/40 hover:bg-sky/40"><ChevronUp className="h-4 w-4" /></button>
                          <button onClick={(e) => { e.stopPropagation(); moveQuestion(q.question_id, 1); }} className="rounded p-1 text-ink/40 hover:bg-sky/40"><ChevronDown className="h-4 w-4" /></button>
                          <button onClick={(e) => { e.stopPropagation(); setSelectedQid(q.question_id); }} title="Advanced settings" data-testid={`question-settings-${gi}`} className={`rounded p-1 ${isSel ? "text-loop" : "text-ink/40 hover:text-loop"}`}><Settings2 className="h-4 w-4" /></button>
                          <button onClick={(e) => { e.stopPropagation(); duplicateQuestion(q.question_id); }} title="Duplicate" data-testid={`question-duplicate-${gi}`} className="rounded p-1 text-ink/40 hover:text-loop"><Copy className="h-4 w-4" /></button>
                          <button onClick={(e) => { e.stopPropagation(); deleteQuestion(q.question_id); }} title="Delete" data-testid={`question-delete-${gi}`} className="rounded p-1 text-red-500 hover:bg-red-50"><Trash2 className="h-4 w-4" /></button>
                        </div>
                        <div className="mt-1.5 flex flex-wrap items-center gap-1.5 pl-6 text-[10px]">
                          {q.required && <span className="rounded-full bg-red-50 px-1.5 py-0.5 font-semibold text-red-500">Required</span>}
                          {q.scoring_enabled && <span className="rounded-full bg-amber-50 px-1.5 py-0.5 font-semibold text-amber-600">Scored</span>}
                          {q.field_mapping && <span className="rounded-full bg-sky/40 px-1.5 py-0.5 font-semibold text-loop">→ {q.field_mapping}</span>}
                          {q.category_dependent && <span className="rounded-full bg-violet-50 px-1.5 py-0.5 font-semibold text-violet-600">Category-dependent</span>}
                          {q.conditional?.question_id && <span className="rounded-full bg-indigo-50 px-1.5 py-0.5 font-semibold text-indigo-600">Conditional</span>}
                        </div>
                        {/* inline options */}
                        {OPTION_TYPES.includes(q.question_type) && !q.category_dependent && (
                          <div className="mt-2 rounded-lg bg-mist/40 p-2" onClick={(e) => e.stopPropagation()}>
                            <div className="mb-1 flex items-center justify-between">
                              <p className="text-[11px] font-semibold text-ink/60">Options{q.scoring_enabled ? " & scores" : ""}</p>
                              <button onClick={() => updQById(q.question_id, { options: [...(q.options || []), { value: "", score: 0 }] })} className="text-[11px] font-semibold text-loop hover:underline" data-testid={`add-option-${gi}`}>+ option</button>
                            </div>
                            <div className="space-y-1.5">
                              {(q.options || []).map((o, oi) => (
                                <div key={oi} className="flex items-center gap-2">
                                  <input className={`${inp} flex-1`} placeholder="Option value" value={o.value} onChange={(e) => updQById(q.question_id, { options: q.options.map((x, xi) => xi === oi ? { ...x, value: e.target.value } : x) })} data-testid={`option-${gi}-${oi}`} />
                                  {q.scoring_enabled && <input type="number" className="h-9 w-20 rounded-lg border border-sky px-2 text-sm" value={o.score} onChange={(e) => updQById(q.question_id, { options: q.options.map((x, xi) => xi === oi ? { ...x, score: Number(e.target.value) } : x) })} />}
                                  <button onClick={() => updQById(q.question_id, { options: q.options.filter((_, xi) => xi !== oi) })} className="rounded p-1 text-red-400 hover:bg-red-50"><Trash2 className="h-3.5 w-3.5" /></button>
                                </div>
                              ))}
                              {(q.options || []).length === 0 && <p className="text-[11px] text-ink/40">No options yet.</p>}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </section>
            );
          })}
          {filtering && totalMatches === 0 && <p className="rounded-2xl border border-dashed border-sky py-10 text-center text-sm text-ink/40" data-testid="flow-no-matches">No questions match your search/filters.</p>}
        </main>

        {/* RIGHT — advanced settings drawer */}
        <aside className="w-72 shrink-0" data-testid="flow-right-panel">
          <div className="sticky top-2 rounded-2xl border border-sky/70 bg-white p-3.5">
            {!selectedQ ? (
              <div className="py-10 text-center" data-testid="settings-empty">
                <SlidersHorizontal className="mx-auto h-7 w-7 text-ink/20" />
                <p className="mt-2 text-xs text-ink/40">Select a question to edit its advanced settings.</p>
              </div>
            ) : (
              <div className="space-y-3" data-testid="settings-drawer">
                <div className="flex items-center justify-between">
                  <h4 className="font-sora text-sm font-bold text-navy">Settings</h4>
                  <button onClick={() => setSelectedQid(null)} className="text-ink/40 hover:text-ink"><X className="h-4 w-4" /></button>
                </div>
                <p className="truncate rounded-lg bg-mist/50 px-2 py-1.5 text-xs font-medium text-ink/70">{selectedQ.question_label || "(untitled)"}</p>

                <div>
                  <label className={lbl}>Step / section</label>
                  <select className={inp} value={stepOf(selectedQ)} onChange={(e) => updQById(selectedQ.question_id, { step: e.target.value === STEP_GENERAL ? null : e.target.value })} data-testid="settings-step">
                    {stepOrder.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>

                <div>
                  <label className={lbl}>Maps to field</label>
                  <select className={inp} value={selectedQ.field_mapping || ""} onChange={(e) => updQById(selectedQ.question_id, { field_mapping: e.target.value || null })} data-testid="settings-mapping">
                    {FIELD_MAPPINGS.map((f) => <option key={f} value={f}>{f || "— none —"}</option>)}
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <label className="flex items-center gap-1.5 rounded-lg border border-sky px-2 py-1.5 text-xs text-ink/70"><input type="checkbox" checked={selectedQ.required} onChange={(e) => updQById(selectedQ.question_id, { required: e.target.checked })} data-testid="settings-required" />Required</label>
                  <label className="flex items-center gap-1.5 rounded-lg border border-sky px-2 py-1.5 text-xs text-ink/70"><input type="checkbox" checked={selectedQ.scoring_enabled} onChange={(e) => updQById(selectedQ.question_id, { scoring_enabled: e.target.checked })} data-testid="settings-scored" />Scored</label>
                </div>
                <label className="flex items-center gap-1.5 text-xs text-ink/70"><input type="checkbox" checked={selectedQ.category_dependent} onChange={(e) => updQById(selectedQ.question_id, { category_dependent: e.target.checked })} data-testid="settings-category" />Options depend on Business Segment</label>

                <div>
                  <label className={lbl}>Show only if</label>
                  <select className={`${inp} mb-1.5`} value={selectedQ.conditional?.question_id || ""} data-testid="settings-cond-q"
                    onChange={(e) => updQById(selectedQ.question_id, { conditional: e.target.value ? { question_id: e.target.value, op: selectedQ.conditional?.op || "equals", value: selectedQ.conditional?.value || "" } : null })}>
                    <option value="">— always show —</option>
                    {qIdOptions.filter((o) => o.id !== selectedQ.question_id).map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
                  </select>
                  {selectedQ.conditional?.question_id && (
                    <div className="flex gap-1.5">
                      <select className="h-9 rounded-lg border border-sky px-1.5 text-xs" value={selectedQ.conditional?.op || "equals"} onChange={(e) => updQById(selectedQ.question_id, { conditional: { ...selectedQ.conditional, op: e.target.value } })} data-testid="settings-cond-op">
                        <option value="equals">=</option><option value="not_equals">≠</option><option value="contains">has</option>
                      </select>
                      <input className="h-9 flex-1 rounded-lg border border-sky px-2 text-xs" placeholder="value" value={selectedQ.conditional?.value || ""} onChange={(e) => updQById(selectedQ.question_id, { conditional: { ...selectedQ.conditional, value: e.target.value } })} data-testid="settings-cond-v" />
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </aside>
      </div>
      )}

      {/* Hard rules dialog */}
      <RulesDialog
        open={rulesOpen}
        onOpenChange={setRulesOpen}
        rules={draft.hard_rules}
        meta={meta}
        options={options}
        qIdOptions={qIdOptions}
        onAdd={() => upd({ hard_rules: [...draft.hard_rules, blankRule()] })}
        onUpdate={updR}
        onRemove={(i) => upd({ hard_rules: draft.hard_rules.filter((_, idx) => idx !== i) })}
      />
    </div>
  );
}
